import { useState } from "react";
import { toast } from "sonner";
import { Instagram, MessageSquare } from "lucide-react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { CONFIG, tapInSms } from "@/config";
import Seo from "@/components/Seo";
import Ticker from "@/components/Ticker";
import { FadeUp } from "@/components/Reveal";

export default function TapIn() {
  const [form, setForm] = useState({ name: "", ig_handle: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Name and message are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.post("/inquiries", form);
      toast.success(data.message || "Sent!");
      setForm({ name: "", ig_handle: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-b border-white/30 bg-transparent py-3 text-base outline-none transition-colors placeholder:text-neutral-600 focus:border-yaza-accent";

  return (
    <>
      <Seo title="Tap In — YAZA EXOTICS" description="Tap in to place an order or ask about YAZA drops. Instagram DM or text." />

      <section className="mx-auto max-w-[1600px] px-5 pt-16 pb-8 md:px-10 md:pt-24">
        <FadeUp>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-yaza-accent">Get In Touch</p>
          <h1 className="font-display text-6xl leading-none md:text-8xl">TAP IN</h1>
        </FadeUp>
      </section>

      <Ticker text="TAP IN" speed={60} />

      <section className="mx-auto grid max-w-[1600px] gap-16 px-5 py-16 md:grid-cols-2 md:px-10 md:py-24">
        {/* Direct buttons */}
        <FadeUp>
          <h2 className="font-display text-3xl md:text-4xl">GO STRAIGHT TO SOURCE</h2>
          <p className="mt-4 max-w-md text-yaza-dim">
            Fastest way to order is a DM or a text. We route every drop through Instagram and our text line — no cart, no checkout, just real service.
          </p>
          <div className="mt-8 flex flex-col gap-4">
            <a
              href={CONFIG.instagramDM}
              target="_blank"
              rel="noreferrer"
              data-testid="direct-ig"
              className="flex items-center justify-between rounded-none bg-yaza-accent px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors hover:bg-yaza-accentHover"
            >
              <span className="flex items-center gap-3"><Instagram size={18} /> Instagram DM</span>
              <span>@{CONFIG.instagramHandle}</span>
            </a>
            <a
              href={tapInSms("")}
              data-testid="direct-sms"
              className="flex items-center justify-between rounded-none border border-white/30 px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-yaza-accent hover:text-yaza-accent"
            >
              <span className="flex items-center gap-3"><MessageSquare size={18} /> Text The Line</span>
              <span>{CONFIG.smsNumber}</span>
            </a>
          </div>
        </FadeUp>

        {/* Form */}
        <FadeUp delay={0.1}>
          <form onSubmit={submit} className="flex flex-col gap-6" data-testid="tapin-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500">Name *</label>
              <input className={inputCls} value={form.name} onChange={set("name")} data-testid="tapin-name" placeholder="Your name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500">Instagram Handle</label>
              <input className={inputCls} value={form.ig_handle} onChange={set("ig_handle")} data-testid="tapin-ig" placeholder="@yourhandle" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500">Phone (optional)</label>
              <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="tapin-phone" placeholder="+1 ..." />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500">Message *</label>
              <textarea className={`${inputCls} resize-none`} rows={4} value={form.message} onChange={set("message")} data-testid="tapin-message" placeholder="What are you after?" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="tapin-submit"
              className="mt-2 w-fit rounded-none bg-white px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-yaza-accent disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </FadeUp>
      </section>
    </>
  );
}
